import { ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import { getAuthErrorMessage, AuthError } from '@/lib/auth/domain-validation';
import { performCompleteLogout } from '@/lib/auth/logout';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
}

/**
 * 環境がローカルかどうかを判定する関数
 */
function isLocalEnvironment(): boolean {
  return import.meta.env.VITE_APP_ENV === 'local';
}

/**
 * 認証ガードコンポーネント
 * react-oidc-contextを使用した認証保護ルート
 */
export function ProtectedRoute({ children, requireAuth = true }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isAuthorized, user, signIn } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasTriedAutoSignIn = useRef(false);

  // ローカル環境の場合は認証をスキップ
  if (isLocalEnvironment()) {
    return <>{children}</>;
  }

  const handleLogout = async () => {
    try {
      await performCompleteLogout();
    } catch (error) {
      console.error('認証ガードログアウトエラー:', error);
      // フォールバック: 直接サインインページへリダイレクト
      navigate('/auth/signin', { replace: true });
    }
  };

  useEffect(() => {
    // 認証が必要ない場合はスキップ
    if (!requireAuth) {
      return;
    }

    // ローディング中はスキップ
    if (isLoading) {
      return;
    }

    // 未認証の場合: Cognitoに直接リダイレクト（セッションが残っていれば自動認証）
    if (!isAuthenticated && !hasTriedAutoSignIn.current) {
      hasTriedAutoSignIn.current = true;
      // 認証後に元のURLへ戻れるよう保存
      const returnUrl = window.location.pathname + window.location.search;
      if (returnUrl && returnUrl !== '/' && returnUrl !== '/auth/signin') {
        sessionStorage.setItem('auth_return_url', returnUrl);
      }
      setIsRedirecting(true);
      signIn().catch(() => {
        // signinRedirect失敗時はサインインページへフォールバック
        navigate('/auth/signin', { replace: true });
      });
      return;
    }

    // 認証済みだが、ワークスペースアクセス権限をチェック
    if (isAuthenticated && user) {
      if (!isAuthorized) {
        setAuthError(AuthError.NOT_IN_WORKSPACE);
        return;
      }

      setAuthError(null);
    }
  }, [isAuthenticated, isLoading, isAuthorized, user, navigate, pathname, requireAuth]);

  // 認証エラーの表示
  if (authError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={3}
        padding={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            アクセス権限エラー
          </Typography>
          <Typography variant="body1" gutterBottom>
            {getAuthErrorMessage(authError)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            現在のアカウント: {user?.email}
          </Typography>
        </Alert>
        <Box display="flex" gap={2}>
          <Button variant="contained" onClick={handleLogout}>
            別のアカウントでログイン
          </Button>
          <Button variant="outlined" onClick={() => navigate('/auth/signin', { replace: true })}>
            ログイン画面に戻る
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>認証状態を確認中...</Typography>
      </Box>
    );
  }

  if (requireAuth && (!isAuthenticated || isRedirecting)) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>サインインページにリダイレクト中...</Typography>
      </Box>
    );
  }

  return <>{children}</>;
}
